import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowRight, ChevronRight, Shield, Eye, DollarSign, Search, Zap, Lock, BarChart3, AlertTriangle, CheckCircle, Play, Menu, X } from 'lucide-react';

const SHIELD_LOGO = 'https://customer-assets.emergentagent.com/job_creator-hub-699/artifacts/znp5fjmw_image_7fc4ee3c-94ac-47f2-acda-a583cc0a2f45.png';
const HERO_BANNER = 'https://customer-assets.emergentagent.com/job_creator-hub-699/artifacts/ocymvu9u_image_7f191b4f-4fe0-4f04-a6c2-48d4d45f7e41.png';
const SXI_LOGO = 'https://customer-assets.emergentagent.com/job_creator-hub-699/artifacts/tamrp5bn_image_c107f749-6e21-445d-b76d-d32f1f78a0b7.png';
const QUADRA_LOGO = 'https://customer-assets.emergentagent.com/job_creator-hub-699/artifacts/xofpw4gy_image_0852ba71-f307-4acc-b4f4-a151e3175634.png';
const SMITHERS_AVATAR = 'https://customer-assets.emergentagent.com/job_creator-hub-699/artifacts/q4qvsir8_image_6c687a54-4aa9-4832-83c4-db83dd08084a.png';
const SMITHERS_XO = 'https://customer-assets.emergentagent.com/job_creator-hub-699/artifacts/v9a4pmhn_image_9db183aa-9389-4fc5-a837-5d011fd63d94.png';
const NEON_SIGN = '/images/neon-sign.png';

const TESTIMONIALS = [
  { name: 'Jaylen Carter', initials: 'JC', role: 'Music Producer', quote: 'This was an absolute lifeline. I had no idea three brands were running my beats in ads. SMITHERS found them all.', color: '#00F0FF' },
  { name: 'Marissa Vox', initials: 'MV', role: 'Visual Artist', quote: 'I thought my work disappeared into the void. Turns out it was making someone else money. Not anymore.', color: '#FF00E5' },
  { name: 'D. Holloway', initials: 'DH', role: 'Photographer', quote: 'Who knew that good still exists? This platform literally paid for itself in the first week.', color: '#8B5CF6' },
  { name: 'Tanya Reeves', initials: 'TR', role: 'Content Creator', quote: 'The Velvet Hammer is no joke. Settlement notice sent, payment received, all within 72 hours.', color: '#22C55E' },
  { name: 'Kai Nguyen', initials: 'KN', role: 'Filmmaker', quote: 'I found a Fortune 500 company using my footage in a national campaign. SMITHERS handled everything.', color: '#F59E0B' },
  { name: 'Rachel Stone', initials: 'RS', role: 'Songwriter', quote: 'A complete gift. I recovered $4,200 from content I forgot I even made. This is the future.', color: '#00F0FF' },
  { name: 'Marcus Bell', initials: 'MB', role: 'Graphic Designer', quote: 'Every creator needs this. Period. Stop giving your work away for free.', color: '#FF00E5' },
];

/* HeroText: per-character peel animation. 
   'out' = letters peel off in reverse (last char first), lifting up + sweeping left
   'in' = letters arrive one by one from the left
*/
function HeroText({ text, phase }) {
  const words = text.split(' ');
  const dashIdx = text.indexOf('—');
  let charCount = 0;
  const totalChars = text.replace(/ /g, '').length;

  return (
    <span style={{ display: 'inline', transformStyle: 'preserve-3d' }}>
      {words.map((word, wi) => {
        const wordStartPos = text.indexOf(word, wi === 0 ? 0 : text.indexOf(words[wi - 1]) + words[wi - 1].length);
        const isAfterDash = dashIdx >= 0 && wordStartPos >= dashIdx;
        const afterDash = dashIdx >= 0 ? text.substring(dashIdx) : '';
        const lastSpaceAfter = afterDash.lastIndexOf(' ');
        const lastWordStart = dashIdx >= 0 ? dashIdx + lastSpaceAfter : -1;
        const isLastWord = dashIdx >= 0 && wordStartPos >= lastWordStart && lastSpaceAfter > 0;

        let colorClass = 'text-white';
        if (isLastWord) colorClass = 'neon-magenta';
        else if (isAfterDash) colorClass = 'neon-cyan';

        const wordChars = word.split('');
        const startCharIdx = charCount;
        charCount += wordChars.length;

        return (
          <span key={wi} style={{ display: 'inline-block', whiteSpace: 'nowrap', marginRight: '0.3em' }}>
            {wordChars.map((char, ci) => {
              const globalIdx = startCharIdx + ci;
              let style = { display: 'inline-block' };

              if (phase === 'out') {
                const reverseIdx = totalChars - 1 - globalIdx;
                style.animation = `charPeelOut 0.6s cubic-bezier(0.55, 0, 1, 0.45) ${reverseIdx * 0.02}s both`;
              } else if (phase === 'in') {
                style.animation = `charPeelIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${globalIdx * 0.02}s both`;
              }

              return (
                <span key={`${phase}-${globalIdx}`} className={colorClass} style={style}>
                  {char}
                </span>
              );
            })}
          </span>
        );
      })}
    </span>
  );
}

const TESTIMONIALS_DATA = [
  { name: 'Jaylen Carter', initials: 'JC', role: 'Music Producer', quote: 'This was an absolute lifeline. I had no idea three brands were running my beats in ads. SMITHERS found them all.', color: '#00F0FF' },
  { name: 'Marissa Vox', initials: 'MV', role: 'Visual Artist', quote: 'I thought my work disappeared into the void. Turns out it was making someone else money. Not anymore.', color: '#FF00E5' },
  { name: 'D. Holloway', initials: 'DH', role: 'Photographer', quote: 'Who knew that good still exists? This platform literally paid for itself in the first week.', color: '#8B5CF6' },
  { name: 'Tanya Reeves', initials: 'TR', role: 'Content Creator', quote: 'The Velvet Hammer is no joke. Settlement notice sent, payment received, all within 72 hours.', color: '#22C55E' },
  { name: 'Kai Nguyen', initials: 'KN', role: 'Filmmaker', quote: 'I found a Fortune 500 company using my footage in a national campaign. SMITHERS handled everything.', color: '#F59E0B' },
  { name: 'Rachel Stone', initials: 'RS', role: 'Songwriter', quote: 'A complete gift. I recovered $4,200 from content I forgot I even made. This is the future.', color: '#00F0FF' },
  { name: 'Marcus Bell', initials: 'MB', role: 'Graphic Designer', quote: 'Every creator needs this. Period. Stop giving your work away for free.', color: '#FF00E5' },
];

/* Strips the checkered pattern from a neon sign image */
function NeonSignCanvas({ src, isOn, isFlickering, className }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i], g = data[i+1], b = data[i+2];
        const max = Math.max(r, g, b), min = Math.min(r, g, b);
        const diff = max - min;
        // Checkered pattern: near-gray pixels (R≈G≈B). Neon: highly saturated.
        // Remove any pixel where color channels are close together (gray)
        if (diff < 40) {
          data[i+3] = 0; // fully transparent
        } else if (diff < 60) {
          // Fade out semi-gray pixels
          data[i+3] = Math.floor(data[i+3] * ((diff - 40) / 20));
        }
      }
      ctx.putImageData(imageData, 0, 0);
    };
    img.src = src;
  }, [src]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{
        maxWidth: '320px',
        width: '100%',
        transition: 'all 0.3s ease',
        filter: isFlickering
          ? 'brightness(3) blur(2px)'
          : isOn
            ? 'brightness(1.1) drop-shadow(0 0 20px rgba(0,240,255,0.5)) drop-shadow(0 0 40px rgba(255,0,229,0.3))'
            : 'brightness(0.12) grayscale(0.9)',
        opacity: isFlickering ? 0.6 : 1,
      }}
    />
  );
}

const FLOW_STEPS = [
  { step: '01', title: 'CONNECT YOUR CONTENT', desc: 'Link your platforms and upload your media library. SMITHERS catalogs everything you own.', icon: Lock, color: 'cyan',
    detail: 'Connect YouTube, TikTok, Instagram, Twitter/X and more. SMITHERS fingerprints every piece of content — audio, video, images, text — and stores it in your QUADRA Vault with cryptographic proof of ownership.' },
  { step: '02', title: 'SMITHERS SCANS THE INTERNET', desc: 'Our AI crawls billions of pages, social platforms, and media databases to find unauthorized uses.', icon: Search, color: 'magenta',
    detail: 'The Oracle Engine connects to platform APIs, runs Acoustic Content Recognition on commercial videos, and performs NLP scraping on captions and ad copy. Only commercially monetized content is targeted — no noise, no false positives.' },
  { step: '03', title: 'REVIEW YOUR RESULTS', desc: 'See exactly who is using your work, where, and how much revenue you\'re missing.', icon: Eye, color: 'purple',
    detail: 'Your dashboard shows every violation: the platform, the brand, the campaign URL, the match confidence percentage, and the estimated revenue owed. Everything is verified by the QUADRA Engineer Core before it reaches you.' },
  { step: '04', title: 'RECOVER YOUR MONEY', desc: 'Activate SMITHERS to send automated DMCA notices and collect owed royalties on your behalf.', icon: DollarSign, color: 'cyan',
    detail: 'SMITHERS sends legally compliant settlement notices offering brands a Retroactive Perpetual Commercial License. Pay within 48 hours or face automated DMCA takedowns. Most brands pay immediately — it\'s cheaper than losing a $200K ad campaign.' },
];

const FEATURES = [
  { icon: Search, title: 'Deep Web Scanning', desc: 'AI-powered crawling across every major platform', color: 'cyan',
    detail: 'The Oracle Engine connects to YouTube Data API, TikTok Marketing API, and Meta Ad Library — filtering for commercially monetized content only.' },
  { icon: AlertTriangle, title: 'Infringement Detection', desc: 'Automated identification of unauthorized use', color: 'magenta',
    detail: 'Acoustic Content Recognition matches audio even when pitch-shifted or sped up. Visual fingerprinting catches crops, filters, and re-edits.' },
  { icon: DollarSign, title: 'Revenue Recovery', desc: 'Collect royalties you didn\'t know you were owed', color: 'cyan',
    detail: 'SMITHERS calculates your owed revenue based on ad spend data, platform monetization rates, and campaign performance metrics.' },
  { icon: Shield, title: 'DMCA Enforcement', desc: 'Automated takedown notices and legal protection', color: 'magenta',
    detail: 'Legally compliant notices under 17 U.S.C. § 512 with settlement offers protected under Federal Rule of Evidence 408.' },
  { icon: BarChart3, title: 'Usage Analytics', desc: 'See where and how your content spreads', color: 'purple',
    detail: 'Real-time dashboards showing geographic reach, platform distribution, and monetization potential of your IP.' },
  { icon: Zap, title: 'Real-Time Alerts', desc: 'Instant notifications when new uses are detected', color: 'cyan',
    detail: 'Push notifications, email alerts, and SMS when SMITHERS detects a new unauthorized use of your content.' },
];

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const howItWorksRef = useRef(null);
  const [expandedStep, setExpandedStep] = useState(null);
  const [expandedFeature, setExpandedFeature] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [portalTarget, setPortalTarget] = useState(null); // universe transition

  const toggleStep = (i) => {
    setExpandedStep(expandedStep === i ? null : i);
  };

  const toggleFeature = (i) => {
    setExpandedFeature(expandedFeature === i ? null : i);
  };

  const [showSmithersHQ, setShowSmithersHQ] = useState(false);
  const [heroLineIdx, setHeroLineIdx] = useState(0);
  const [heroPhase, setHeroPhase] = useState('in'); // 'in' | 'out' | 'visible'

  const HERO_LINES = [
    'Find Out Who Is Using Your Work — And Get Paid For It',
    'Your Content Has Hidden Revenue — Let Us Find It',
    'Stop Letting Brands Steal Your Work — Start Getting Compensated',
    'The Internet Owes You Money — SMITHERS Will Collect It',
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setHeroPhase('out');
      setTimeout(() => {
        setHeroLineIdx(p => (p + 1) % HERO_LINES.length);
        setHeroPhase('in');
        setTimeout(() => setHeroPhase('visible'), 2000);
      }, 2000);
    }, 15000);
    return () => clearInterval(timer);
  }, [HERO_LINES.length]);

  const [neonVisible, setNeonVisible] = useState(false);
  const [neonFlicker, setNeonFlicker] = useState(false);
  const [cordPulled, setCordPulled] = useState(false);

  const pullCord = (e) => {
    if (e) e.stopPropagation();
    setCordPulled(true);
    setTimeout(() => setCordPulled(false), 400);
    setNeonFlicker(true);

    setTimeout(() => {
      setNeonFlicker(false);
      setNeonVisible(prev => {
        const next = !prev;
        // Dispatch event to SmithersOrb
        window.dispatchEvent(new CustomEvent('smithers-neon-toggle', { detail: { on: next } }));
        return next;
      });
    }, 300);
  };

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToHowItWorks = () => {
    howItWorksRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Universe Portal Transition */}
      {portalTarget && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center"
          style={{ background: '#000', animation: 'portalFadeIn 0.4s ease-out' }}
          data-testid="universe-portal">
          <div className="text-center" style={{ animation: 'portalPulse 1.5s ease-in-out' }}>
            <div className="w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center"
              style={{
                background: `radial-gradient(circle, ${portalTarget.color}30, transparent)`,
                border: `2px solid ${portalTarget.color}60`,
                boxShadow: `0 0 60px ${portalTarget.color}30, 0 0 120px ${portalTarget.color}15`,
              }}>
              <div className="w-3 h-3 rounded-full" style={{ background: portalTarget.color, boxShadow: `0 0 20px ${portalTarget.color}` }} />
            </div>
            <p className="font-label text-xs mb-2" style={{ color: portalTarget.color }}>ENTERING</p>
            <h2 className="text-2xl font-black mb-2" style={{ fontFamily: 'Orbitron', color: portalTarget.color }}>
              {portalTarget.name}
            </h2>
            <p className="text-sm text-gray-500">{portalTarget.tagline}</p>
          </div>
        </div>
      )}

      {/* Glass Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="font-brand text-sm font-black">
              <span className="text-[--ch-cyan]">SMITHERS</span> <span className="text-[--ch-magenta]">XI</span>
            </span>
          </div>
          <div className="hidden md:flex items-center gap-5">
            <button onClick={scrollToHowItWorks}
              className="font-label text-[--ch-text-secondary] hover:text-white transition-colors">
              HOW IT WORKS
            </button>
            <button onClick={() => navigate('/subscribe')}
              className="font-label text-[--ch-text-secondary] hover:text-white transition-colors">
              PRICING
            </button>
            <div className="w-px h-3 bg-[--ch-border]" />
            <button onClick={() => {
                setPortalTarget({ name: "THE CREATOR'S AGENT", color: '#00F0FF', tagline: "Entering the Creator's Universe" });
                setTimeout(() => { window.open('https://thecreatorsagent.com', '_blank'); setPortalTarget(null); }, 1800);
              }}
              data-testid="nav-creators-agent"
              className="group px-2.5 py-1.5 rounded-lg transition-all hover:bg-[rgba(0,240,255,0.04)]">
              <span className="font-brand text-[10px] font-black tracking-widest group-hover:tracking-[0.18em] transition-all"
                style={{ color: '#00F0FF' }}>CREATOR'S AGENT</span>
            </button>
            <button onClick={() => {
                setPortalTarget({ name: 'QUADRA CORE', color: '#D4AF37', tagline: 'Entering the Quadra Core Universe' });
                setTimeout(() => { navigate('/scan'); setPortalTarget(null); }, 1800);
              }}
              data-testid="nav-quadra-core"
              className="group px-2.5 py-1.5 rounded-lg transition-all hover:bg-[rgba(212,175,55,0.04)]">
              <span className="font-brand text-[10px] font-black tracking-widest group-hover:tracking-[0.18em] transition-all"
                style={{ color: '#D4AF37' }}>QUADRA CORE</span>
            </button>
            <button onClick={() => {
                setPortalTarget({ name: 'SMITHERS XI', color: '#FF00E5', tagline: 'Entering the Smithers XI Universe' });
                setTimeout(() => { navigate('/enforcement'); setPortalTarget(null); }, 1800);
              }}
              data-testid="nav-smithers-xi"
              className="group px-2.5 py-1.5 rounded-lg transition-all hover:bg-[rgba(255,0,229,0.04)]">
              <span className="font-brand text-[10px] font-black tracking-widest group-hover:tracking-[0.18em] transition-all"
                style={{ color: '#FF00E5' }}>SMITHERS XI</span>
            </button>
            <button onClick={() => navigate('/shop')}
              className="font-label hover:text-[--ch-warning] transition-colors"
              style={{ color: '#D4AF37' }}>
              SHOP
            </button>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="hero-go-premium-btn" onClick={() => navigate(user ? '/scan' : '/auth')}
              className="btn-neon px-5 py-2 rounded-full text-sm font-semibold">
              {user ? 'MY DASHBOARD' : 'GET STARTED'}
            </button>
            <button
              data-testid="landing-mobile-menu"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-[--ch-text-secondary] hover:text-white"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-16 left-0 right-0 glass border-b border-[--ch-border] animate-fade-in-up" data-testid="landing-mobile-dropdown">
            <div className="p-4 space-y-2">
              <button onClick={() => { scrollToHowItWorks(); setMobileMenuOpen(false); }}
                className="block w-full text-left px-3 py-2.5 rounded-lg font-label text-[--ch-text-secondary] hover:text-white hover:bg-[rgba(255,255,255,0.03)] transition-colors">
                HOW IT WORKS
              </button>
              <button onClick={() => { navigate('/subscribe'); setMobileMenuOpen(false); }}
                className="block w-full text-left px-3 py-2.5 rounded-lg font-label text-[--ch-text-secondary] hover:text-white hover:bg-[rgba(255,255,255,0.03)] transition-colors">
                PRICING
              </button>
              <div className="border-t border-[--ch-border] my-2" />
              <p className="px-3 text-[9px] font-label text-gray-600 mb-1">UNIVERSES</p>
              <button onClick={() => {
                  setMobileMenuOpen(false);
                  setPortalTarget({ name: "THE CREATOR'S AGENT", color: '#00F0FF', tagline: "Entering the Creator's Universe" });
                  setTimeout(() => { window.open('https://thecreatorsagent.com', '_blank'); setPortalTarget(null); }, 1800);
                }}
                className="block w-full text-left px-3 py-2.5 rounded-lg font-brand text-[10px] font-black tracking-widest hover:bg-[rgba(0,240,255,0.03)] transition-colors"
                style={{ color: '#00F0FF' }}>
                CREATOR'S AGENT
              </button>
              <button onClick={() => {
                  setMobileMenuOpen(false);
                  setPortalTarget({ name: 'QUADRA CORE', color: '#D4AF37', tagline: 'Entering the Quadra Core Universe' });
                  setTimeout(() => { navigate('/scan'); setPortalTarget(null); }, 1800);
                }}
                className="block w-full text-left px-3 py-2.5 rounded-lg font-brand text-[10px] font-black tracking-widest hover:bg-[rgba(212,175,55,0.03)] transition-colors"
                style={{ color: '#D4AF37' }}>
                QUADRA CORE
              </button>
              <button onClick={() => {
                  setMobileMenuOpen(false);
                  setPortalTarget({ name: 'SMITHERS XI', color: '#FF00E5', tagline: 'Entering the Smithers XI Universe' });
                  setTimeout(() => { navigate('/enforcement'); setPortalTarget(null); }, 1800);
                }}
                className="block w-full text-left px-3 py-2.5 rounded-lg font-brand text-[10px] font-black tracking-widest hover:bg-[rgba(255,0,229,0.03)] transition-colors"
                style={{ color: '#FF00E5' }}>
                SMITHERS XI
              </button>
              <button onClick={() => { navigate('/shop'); setMobileMenuOpen(false); }}
                className="block w-full text-left px-3 py-2.5 rounded-lg font-label hover:bg-[rgba(255,255,255,0.03)] transition-colors"
                style={{ color: '#D4AF37' }}>
                SHOP
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_BANNER} alt="" className="w-full h-full object-cover" style={{ transform: `translateY(${scrollY * 0.15}px)` }} />
          <div className="absolute inset-0 bg-gradient-to-r from-[--ch-bg-primary] via-[rgba(13,13,13,0.8)] to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[--ch-bg-primary] via-transparent to-[rgba(13,13,13,0.4)]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-24">
          <div className="max-w-2xl">
            {/* Micro label */}
            <button
              onClick={() => setShowSmithersHQ(true)}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[--ch-cyan] bg-[rgba(0,240,255,0.05)] mb-6 animate-fade-in-up cursor-pointer hover:bg-[rgba(0,240,255,0.1)] hover:scale-105 transition-all group"
              data-testid="micro-label"
            >
              <img src={SMITHERS_XO} alt="SMITHERS" className="w-6 h-6 rounded-full object-cover object-top transition-transform group-hover:scale-110" style={{ border: '1px solid rgba(0,240,255,0.3)' }} />
              <span className="font-label text-[--ch-cyan]">Meet your digital advocate</span>
              <ChevronRight size={12} className="text-[--ch-cyan] group-hover:translate-x-0.5 transition-transform" />
            </button>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1] animate-fade-in-up stagger-1" data-testid="hero-title" style={{ minHeight: '140px', perspective: '800px' }}>
              <HeroText text={HERO_LINES[heroLineIdx]} phase={heroPhase} />
            </h1>

            <p className="mt-6 text-lg text-[--ch-text-secondary] font-normal leading-relaxed max-w-lg animate-fade-in-up stagger-2" data-testid="hero-subhead">
              SMITHERS scans the internet and recovers the money you didn't even know you were owed.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mt-8 animate-fade-in-up stagger-3">
              <button data-testid="hero-scan-btn" onClick={() => navigate(user ? '/scan' : '/auth')}
                className="btn-neon px-8 py-4 rounded-full text-sm font-semibold flex items-center justify-center gap-2">
                Scan My Content <ArrowRight size={16} />
              </button>
              <button data-testid="hero-how-it-works-btn" onClick={scrollToHowItWorks}
                className="btn-outline-neon px-8 py-4 rounded-full text-sm font-semibold flex items-center justify-center gap-2">
                See How It Works <ChevronRight size={16} />
              </button>
            </div>

            {/* Social proof */}
            <div className="flex items-center gap-3 mt-8 animate-fade-in-up stagger-4" data-testid="social-proof">
              <CheckCircle size={16} className="text-[--ch-success]" />
              <span className="text-sm font-semibold text-[--ch-success]">Creators have recovered $10,000+ in unclaimed royalties</span>
            </div>

            {/* Secured by badges */}
            <div className="flex items-center gap-3 mt-8 animate-fade-in-up stagger-5">
              <span className="font-label text-[10px] text-[--ch-text-secondary]">POWERED BY</span>
              <span className="font-brand text-xs text-[--ch-cyan] opacity-50">SMITHERS XI</span>
              <div className="w-px h-3 bg-[--ch-border]" />
              <span className="font-brand text-xs text-[--ch-purple] opacity-50">QUADRA CORE</span>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Carousel */}
      <section className="py-10 border-t border-[--ch-border] overflow-hidden relative" data-testid="testimonials-section">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-0 top-0 bottom-0 w-32 z-10" style={{ background: 'linear-gradient(90deg, #0D0D0D, transparent)' }} />
          <div className="absolute right-0 top-0 bottom-0 w-32 z-10" style={{ background: 'linear-gradient(270deg, #0D0D0D, transparent)' }} />
        </div>
        <div className="flex animate-scroll-x">
          {[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
            <div key={i} className="shrink-0 w-[320px] mx-3 px-5 py-4 rounded-xl" style={{ background: 'rgba(20,20,20,0.6)', border: '1px solid #1A1A1A' }}>
              <div className="flex items-center gap-2 mb-2.5">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: `${t.color}15`, color: t.color, border: `1px solid ${t.color}30` }}>
                  {t.initials}
                </div>
                <div>
                  <p className="text-xs font-medium text-white">{t.name}</p>
                  <p className="text-[10px]" style={{ color: '#6B7280' }}>{t.role}</p>
                </div>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: '#9CA3AF', fontFamily: "'Jost', sans-serif" }}>
                "{t.quote}"
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Video Lander */}
      <section className="py-16 px-6 border-t border-[--ch-border]" data-testid="video-lander-section">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-wide mb-3">
            <span className="text-white">See </span>
            <span className="neon-cyan">SMITHERS</span>
            <span className="text-white"> In Action</span>
          </h2>
          <p className="text-sm text-[--ch-text-secondary] mb-8 max-w-md mx-auto">
            Watch how creators are protecting their IP and recovering revenue they never knew existed.
          </p>
          <div className="aspect-video rounded-2xl overflow-hidden relative group cursor-pointer" style={{ background: '#0A0A0C', border: '1px solid #1A1A1A', boxShadow: '0 0 40px rgba(0,240,255,0.05)' }} data-testid="video-placeholder">
            {/* Placeholder — replace src with your actual video URL */}
            <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
              <div className="w-16 h-16 rounded-full flex items-center justify-center transition-all group-hover:scale-110"
                style={{ background: 'linear-gradient(135deg, rgba(0,240,255,0.2), rgba(255,0,229,0.2))', border: '2px solid rgba(0,240,255,0.3)', backdropFilter: 'blur(8px)' }}>
                <Play size={28} className="text-white ml-1" />
              </div>
              <p className="mt-4 font-label text-[10px]" style={{ color: '#6B7280' }}>VIDEO COMING SOON</p>
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-transparent to-transparent opacity-60" />
            <img src={HERO_BANNER} alt="" className="w-full h-full object-cover opacity-20 group-hover:opacity-30 transition-opacity" />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section ref={howItWorksRef} className="py-24 px-6 bg-texture" data-testid="how-it-works-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-wide">
              <span className="text-white">How </span>
              <span className="neon-cyan">SMITHERS</span>
              <span className="text-white"> Works</span>
            </h2>
            <p className="text-base text-[--ch-text-secondary] mt-3 max-w-md mx-auto">
              From scan to payday in four simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {FLOW_STEPS.map((s, i) => {
              const isExpanded = expandedStep === i;
              return (
                <div
                  key={i}
                  onClick={() => toggleStep(i)}
                  className={`ch-card p-6 relative overflow-hidden animate-fade-in-up cursor-pointer select-none
                    transition-all duration-300 ease-out
                    md:hover:scale-[1.04] md:hover:shadow-[0_0_30px_rgba(0,240,255,0.12)] md:hover:border-[rgba(0,240,255,0.3)]
                    ${isExpanded ? 'scale-[1.02] shadow-[0_0_25px_rgba(0,240,255,0.1)] border-[rgba(0,240,255,0.3)]' : ''}
                  `}
                  style={{ animationDelay: `${i * 0.1}s` }}
                  data-testid={`flow-step-${i}`}
                >
                  <div className="absolute top-4 right-4 text-[48px] font-black opacity-[0.04] leading-none font-brand">{s.step}</div>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 transition-all duration-300 ${
                    isExpanded ? 'scale-110' : ''
                  } ${
                    s.color === 'cyan' ? 'bg-[rgba(0,240,255,0.1)] text-[--ch-cyan]' :
                    s.color === 'magenta' ? 'bg-[rgba(255,0,229,0.1)] text-[--ch-magenta]' :
                    'bg-[rgba(139,92,246,0.1)] text-[--ch-purple]'
                  }`}>
                    <s.icon size={20} />
                  </div>
                  <p className="font-label text-[--ch-cyan] mb-2">STEP {s.step}</p>
                  <h3 className="text-base font-bold tracking-wide mb-2">{s.title}</h3>
                  <p className="text-sm text-[--ch-text-secondary] leading-relaxed">{s.desc}</p>

                  {/* Expanded detail - slides open on click */}
                  <div className={`overflow-hidden transition-all duration-300 ease-out ${
                    isExpanded ? 'max-h-40 opacity-100 mt-4 pt-4 border-t border-[--ch-border]' : 'max-h-0 opacity-0 mt-0 pt-0'
                  }`}>
                    <p className="text-sm text-[--ch-text-secondary] leading-relaxed">{s.detail}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 px-6 border-t border-[--ch-border]" data-testid="features-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-wide">
              <span className="text-white">Your Content.</span>{' '}
              <span className="neon-magenta">Your Money.</span>
            </h2>
            <p className="text-base text-[--ch-text-secondary] mt-3">
              Everything you need to protect and monetize your work
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => {
              const isExpanded = expandedFeature === i;
              return (
                <div
                  key={i}
                  onClick={() => toggleFeature(i)}
                  className={`ch-card p-5 animate-fade-in-up cursor-pointer select-none
                    transition-all duration-300 ease-out
                    md:hover:scale-[1.05] md:hover:shadow-[0_0_25px_rgba(0,240,255,0.1)] md:hover:border-[rgba(0,240,255,0.3)]
                    ${isExpanded ? 'scale-[1.02] shadow-[0_0_20px_rgba(0,240,255,0.08)] border-[rgba(0,240,255,0.3)]' : ''}
                  `}
                  style={{ animationDelay: `${i * 0.06}s` }}
                  data-testid={`feature-card-${i}`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 transition-all duration-300 ${
                    isExpanded ? 'scale-110' : ''
                  } ${
                    f.color === 'cyan' ? 'bg-[rgba(0,240,255,0.1)] text-[--ch-cyan]' :
                    f.color === 'magenta' ? 'bg-[rgba(255,0,229,0.1)] text-[--ch-magenta]' :
                    'bg-[rgba(139,92,246,0.1)] text-[--ch-purple]'
                  }`}>
                    <f.icon size={18} />
                  </div>
                  <h3 className="text-sm font-bold tracking-wide mb-1">{f.title}</h3>
                  <p className="text-sm text-[--ch-text-secondary]">{f.desc}</p>

                  {/* Expanded detail */}
                  <div className={`overflow-hidden transition-all duration-300 ease-out ${
                    isExpanded ? 'max-h-32 opacity-100 mt-3 pt-3 border-t border-[--ch-border]' : 'max-h-0 opacity-0 mt-0 pt-0'
                  }`}>
                    <p className="text-sm text-[--ch-text-secondary] leading-relaxed">{f.detail}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 border-t border-[--ch-border] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-[--ch-cyan] rounded-full opacity-[0.02] blur-[120px]" />
          <div className="absolute bottom-0 right-1/3 w-[500px] h-[500px] bg-[--ch-magenta] rounded-full opacity-[0.02] blur-[120px]" />
        </div>
        <div className="max-w-3xl mx-auto text-center relative z-10">
          {/* XO Suit Smithers with animated rings */}
          <div className="relative w-44 h-44 mx-auto mb-8">
            {/* Outer rotating ring - cyan */}
            <div className="absolute inset-[-12px] rounded-full animate-spin-slow"
              style={{
                border: '2px solid transparent',
                borderTopColor: '#00F0FF',
                borderRightColor: 'rgba(0,240,255,0.3)',
                filter: 'drop-shadow(0 0 6px rgba(0,240,255,0.4))',
                animationDuration: '8s',
              }} />
            {/* Middle rotating ring - magenta (reverse) */}
            <div className="absolute inset-[-6px] rounded-full"
              style={{
                border: '1.5px solid transparent',
                borderBottomColor: '#FF00E5',
                borderLeftColor: 'rgba(255,0,229,0.3)',
                filter: 'drop-shadow(0 0 6px rgba(255,0,229,0.4))',
                animation: 'spin 6s linear infinite reverse',
              }} />
            {/* Inner pulsing ring - cyan */}
            <div className="absolute inset-0 rounded-full animate-pulse-glow"
              style={{
                border: '1px solid rgba(0,240,255,0.3)',
                boxShadow: '0 0 15px rgba(0,240,255,0.1), inset 0 0 15px rgba(0,240,255,0.05)',
              }} />
            {/* XO Suit Image */}
            <img src={SMITHERS_XO} alt="SMITHERS XO" className="w-full h-full rounded-full object-cover object-top relative z-10"
              style={{
                border: '2px solid rgba(0,240,255,0.2)',
                boxShadow: '0 0 30px rgba(0,240,255,0.15), 0 0 60px rgba(255,0,229,0.08)',
              }}
              data-testid="smithers-xo-avatar"
            />
            {/* Corner tech indicators */}
            {[
              { top: '4px', right: '4px' },
              { bottom: '4px', left: '4px' },
            ].map((pos, i) => (
              <div key={i} className="absolute w-2 h-2 z-20" style={pos}>
                <div className="w-full h-full rounded-full animate-pulse"
                  style={{
                    background: i === 0 ? '#00F0FF' : '#FF00E5',
                    boxShadow: `0 0 8px ${i === 0 ? '#00F0FF' : '#FF00E5'}`,
                  }} />
              </div>
            ))}
          </div>

          {/* HUD label */}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-6"
            style={{ background: 'rgba(0,240,255,0.05)', border: '1px solid rgba(0,240,255,0.15)' }}>
            <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#00F0FF', boxShadow: '0 0 6px #00F0FF' }} />
            <span className="font-label text-[10px]" style={{ color: '#00F0FF' }}>SMITHERS XI · ONLINE</span>
          </div>

          <h2 className="text-3xl sm:text-4xl font-bold tracking-wide">
            Stop <span className="neon-magenta" style={{ textShadow: '0 0 20px rgba(255,0,229,0.4), 0 0 40px rgba(255,0,229,0.15)' }}>Losing</span> <span className="neon-cyan">Money</span>
          </h2>
          <p className="mt-4 text-base text-[--ch-text-secondary] max-w-lg mx-auto">
            Your content is being used without permission right now. SMITHERS finds it and gets you paid.
          </p>
          <button data-testid="cta-scan-btn" onClick={() => navigate(user ? '/scan' : '/auth')}
            className="btn-neon px-10 py-4 rounded-full text-sm font-semibold mt-8 inline-flex items-center gap-2">
            Scan My Content <ArrowRight size={16} />
          </button>
          <p className="text-sm text-[--ch-text-secondary] mt-4">Free scan. No credit card required.</p>
        </div>
      </section>

      {/* SMITHERS HQ Overlay */}
      {showSmithersHQ && (
        <div
          className="fixed inset-0 z-[9998] flex items-center justify-center"
          onClick={() => setShowSmithersHQ(false)}
          style={{ background: 'rgba(0,0,0,0.95)', backdropFilter: 'blur(16px)', animation: 'fadeIn 0.3s ease-out' }}
          data-testid="smithers-hq-overlay"
        >
          <div onClick={(e) => e.stopPropagation()} className="max-w-4xl w-full mx-4 relative max-h-[90vh] overflow-y-auto" style={{ animation: 'dropIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)' }}>
            {/* Close button */}
            <button onClick={() => setShowSmithersHQ(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center z-20 hover:bg-[rgba(255,255,255,0.1)] transition-colors"
              style={{ color: '#6B7280' }}>
              &times;
            </button>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-8">
              {/* Left: XO Suit with diagnostic rings */}
              <div className="flex items-center justify-center">
                <div className="relative w-40 h-40 md:w-64 md:h-64">
                  {/* Scanning rings */}
                  <div className="absolute inset-[-20px] rounded-full animate-spin-slow" style={{ border: '1px solid rgba(0,240,255,0.15)', borderTopColor: '#00F0FF', animationDuration: '12s' }} />
                  <div className="absolute inset-[-12px] rounded-full" style={{ border: '1px solid rgba(255,0,229,0.1)', borderBottomColor: '#FF00E5', animation: 'spin 8s linear infinite reverse' }} />
                  <div className="absolute inset-[-4px] rounded-full animate-pulse-glow" style={{ border: '1px solid rgba(0,240,255,0.2)' }} />

                  {/* Corner data readouts */}
                  {[
                    { pos: { top: '-8px', left: '-8px' }, label: 'NEURAL', val: 'ACTIVE' },
                    { pos: { top: '-8px', right: '-8px' }, label: 'THREAT', val: '0.00' },
                    { pos: { bottom: '-8px', left: '-8px' }, label: 'UPTIME', val: '99.97%' },
                    { pos: { bottom: '-8px', right: '-8px' }, label: 'SYNC', val: 'LIVE' },
                  ].map((d, i) => (
                    <div key={i} className="absolute z-20 px-1.5 py-0.5 rounded" style={{ ...d.pos, background: 'rgba(0,0,0,0.8)', border: '1px solid #1A1A1A' }}>
                      <p className="text-[7px] font-label" style={{ color: '#4B5563' }}>{d.label}</p>
                      <p className="text-[9px] font-mono font-bold" style={{ color: '#00F0FF' }}>{d.val}</p>
                    </div>
                  ))}

                  <img src={SMITHERS_XO} alt="SMITHERS XI" className="w-full h-full rounded-full object-cover object-top relative z-10"
                    style={{ border: '2px solid rgba(0,240,255,0.2)', boxShadow: '0 0 40px rgba(0,240,255,0.15), 0 0 80px rgba(0,59,111,0.1)', animation: 'neonBreath 4s ease-in-out infinite' }} />
                </div>
              </div>

              {/* Right: Specs & Capabilities */}
              <div className="flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#00F0FF', boxShadow: '0 0 8px #00F0FF' }} />
                  <span className="font-label text-[10px]" style={{ color: '#00F0FF' }}>SYSTEM DIAGNOSTIC</span>
                </div>
                <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: "'Orbitron', sans-serif", color: '#fff' }}>SMITHERS XI</h2>
                <p className="text-xs mb-6" style={{ color: '#6B7280', fontFamily: "'Jost', sans-serif" }}>Advanced Reasoning &amp; Decision-Making AI Presence</p>

                <div className="space-y-3">
                  {[
                    { label: 'ORACLE ENGINE', desc: 'Scans billions of pages across all platforms', icon: Search, color: '#00F0FF', pct: 97 },
                    { label: 'VELVET HAMMER', desc: 'Automated DMCA enforcement & settlement', icon: Shield, color: '#FF00E5', pct: 94 },
                    { label: 'CONTENT FINGERPRINT', desc: 'Audio, video, image & text identification', icon: Eye, color: '#8B5CF6', pct: 99 },
                    { label: 'REVENUE RECOVERY', desc: 'Retroactive licensing & bounty collection', icon: DollarSign, color: '#22C55E', pct: 91 },
                    { label: 'THREAT ASSESSMENT', desc: 'Real-time monitoring & infringement alerts', icon: AlertTriangle, color: '#F59E0B', pct: 96 },
                  ].map((cap, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-lg transition-all hover:bg-[rgba(255,255,255,0.02)]" style={{ background: 'rgba(20,20,20,0.5)', border: '1px solid #1A1A1A', animationDelay: `${i * 100}ms`, animation: 'fadeIn 0.4s ease-out both' }}>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${cap.color}10`, border: `1px solid ${cap.color}25` }}>
                        <cap.icon className="w-4 h-4" style={{ color: cap.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-bold text-white">{cap.label}</p>
                          <span className="text-[10px] font-mono" style={{ color: cap.color }}>{cap.pct}%</span>
                        </div>
                        <p className="text-[11px] mt-0.5" style={{ color: '#6B7280' }}>{cap.desc}</p>
                        <div className="w-full h-1 rounded-full mt-1.5 overflow-hidden" style={{ background: '#1A1A1A' }}>
                          <div className="h-full rounded-full" style={{ width: `${cap.pct}%`, background: `linear-gradient(90deg, ${cap.color}40, ${cap.color})`, animation: `barFill 1s ease-out ${i * 150}ms both` }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <button onClick={() => { setShowSmithersHQ(false); navigate('/scan'); }}
                  className="btn-neon px-6 py-3 rounded-full text-sm font-semibold mt-6 flex items-center justify-center gap-2 w-full"
                  data-testid="hq-activate-btn">
                  Activate SMITHERS <ArrowRight size={16} />
                </button>
              </div>
            </div>

            <p className="text-center mt-2 text-xs" style={{ color: '#2A2A2A' }}>click outside to close</p>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-[--ch-border] py-8 px-6 relative" data-testid="footer">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 relative">
            <p className="text-sm text-[--ch-text-secondary]">&copy; 2026 SMITHERS XI. Secured by Quadra Core OS.</p>
            {/* Easter egg: tiny cord dangling from the period after "OS" */}
            <div
              className="absolute cursor-pointer group"
              style={{ right: '-8px', top: '100%' }}
              onClick={(e) => { e.stopPropagation(); pullCord(e); }}
              data-testid="neon-pull-string"
            >
              {/* The cord — a nearly invisible thin line */}
              <div className="flex flex-col items-center transition-all duration-200 group-hover:translate-y-1">
                <div className="transition-all duration-300"
                  style={{
                    width: '1px',
                    height: cordPulled ? '20px' : '12px',
                    background: neonVisible
                      ? 'linear-gradient(180deg, #00F0FF33, #00F0FF66)'
                      : 'linear-gradient(180deg, #3A3A3A, #2A2A2A)',
                  }} />
                {/* Tiny bead at the end */}
                <div className="transition-all duration-300"
                  style={{
                    width: '4px',
                    height: '4px',
                    borderRadius: '50%',
                    background: neonVisible ? 'rgba(0,240,255,0.4)' : '#2A2A2A',
                    border: `0.5px solid ${neonVisible ? 'rgba(0,240,255,0.6)' : '#3A3A3A'}`,
                    boxShadow: neonVisible ? '0 0 4px rgba(0,240,255,0.3)' : 'none',
                    transform: cordPulled ? 'translateY(4px)' : 'translateY(0)',
                  }} />
              </div>
            </div>
          </div>
          <div className="flex gap-6">
            <button onClick={() => navigate('/dmca')} className="text-sm text-[--ch-text-secondary] hover:text-[--ch-cyan] transition-colors" data-testid="footer-dmca-link">DMCA</button>
            <button onClick={() => navigate('/dmca')} className="text-sm text-[--ch-text-secondary] hover:text-[--ch-cyan] transition-colors" data-testid="footer-terms-link">Terms</button>
            <button onClick={() => navigate('/dmca')} className="text-sm text-[--ch-text-secondary] hover:text-[--ch-cyan] transition-colors" data-testid="footer-privacy-link">Privacy</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
